// client/src/components/media/MediaViewer.jsx
// Fullscreen media viewer with pinch-to-zoom, double-tap zoom, and pan support
import React, { useRef, useState, useCallback, useEffect } from "react";
import { FaTimes } from "react-icons/fa";

const MediaViewer = ({ src, isVideo = false, subtitleSrc = null, onClose }) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  // Transform state
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  // Touch tracking refs (avoid re-renders during gestures)
  const gestureRef = useRef({
    startDist: 0,
    startScale: 1,
    startMid: { x: 0, y: 0 },
    startTranslate: { x: 0, y: 0 },
    isPinching: false,
    isPanning: false,
    panStart: { x: 0, y: 0 },
    lastTap: 0,
  });

  const clampTranslate = useCallback((tx, ty, s) => {
    if (s <= 1) return { x: 0, y: 0 };
    const el = containerRef.current;
    if (!el) return { x: tx, y: ty };
    const rect = el.getBoundingClientRect();
    const maxX = (rect.width * (s - 1)) / 2;
    const maxY = (rect.height * (s - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, tx)),
      y: Math.max(-maxY, Math.min(maxY, ty)),
    };
  }, []);

  const dist = (t1, t2) =>
    Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

  const mid = (t1, t2) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  });

  const handleTouchStart = useCallback((e) => {
    const g = gestureRef.current;
    if (e.touches.length === 2) {
      e.preventDefault();
      g.isPinching = true;
      g.isPanning = false;
      g.startDist = dist(e.touches[0], e.touches[1]);
      g.startScale = scale;
      g.startMid = mid(e.touches[0], e.touches[1]);
      g.startTranslate = { ...translate };
    } else if (e.touches.length === 1) {
      // Double-tap detection
      const now = Date.now();
      if (now - g.lastTap < 300) {
        e.preventDefault();
        if (scale > 1.1) {
          setScale(1);
          setTranslate({ x: 0, y: 0 });
        } else {
          setScale(2.5);
          // Zoom toward tap point
          const rect = containerRef.current?.getBoundingClientRect();
          if (rect) {
            const tx = (rect.width / 2 - e.touches[0].clientX) * 1.5;
            const ty = (rect.height / 2 - e.touches[0].clientY) * 1.5;
            setTranslate(clampTranslate(tx, ty, 2.5));
          }
        }
        g.lastTap = 0;
        return;
      }
      g.lastTap = now;

      // Start pan if zoomed in
      if (scale > 1.1) {
        g.isPanning = true;
        g.panStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        g.startTranslate = { ...translate };
      }
    }
  }, [scale, translate, clampTranslate]); // eslint-disable-line

  const handleTouchMove = useCallback((e) => {
    const g = gestureRef.current;
    if (g.isPinching && e.touches.length === 2) {
      e.preventDefault();
      const newDist = dist(e.touches[0], e.touches[1]);
      const ratio = newDist / g.startDist;
      const newScale = Math.max(1, Math.min(5, g.startScale * ratio));

      const newMid = mid(e.touches[0], e.touches[1]);
      const dx = newMid.x - g.startMid.x;
      const dy = newMid.y - g.startMid.y;
      const tx = g.startTranslate.x + dx;
      const ty = g.startTranslate.y + dy;

      setScale(newScale);
      setTranslate(clampTranslate(tx, ty, newScale));
    } else if (g.isPanning && e.touches.length === 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - g.panStart.x;
      const dy = e.touches[0].clientY - g.panStart.y;
      const tx = g.startTranslate.x + dx;
      const ty = g.startTranslate.y + dy;
      setTranslate(clampTranslate(tx, ty, scale));
    }
  }, [scale, clampTranslate]); // eslint-disable-line

  const handleTouchEnd = useCallback((e) => {
    const g = gestureRef.current;
    if (e.touches.length < 2) g.isPinching = false;
    if (e.touches.length < 1) g.isPanning = false;
  }, []);

  // Prevent page scroll when viewer is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const transform = `translate(${translate.x}px, ${translate.y}px) scale(${scale})`;

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={(e) => {
        // Close when tapping background (not when zoomed/panning)
        if (scale <= 1.1 && e.target === containerRef.current) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.95)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        touchAction: "none",
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          zIndex: 10000,
          background: "rgba(0,0,0,0.6)",
          border: "none",
          color: "#fff",
          fontSize: 24,
          width: 44,
          height: 44,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
        aria-label="Close"
      >
        <FaTimes />
      </button>

      {isVideo ? (
        <video
          src={src}
          controls
          playsInline
          autoPlay
          crossOrigin="anonymous"
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
          }}
        >
          {subtitleSrc && (
            <track
              kind="subtitles"
              src={subtitleSrc}
              srcLang="en"
              label="English"
              default
            />
          )}
        </video>
      ) : (
        <img
          ref={imgRef}
          src={src}
          alt=""
          draggable={false}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            transform,
            transformOrigin: "center center",
            transition: gestureRef.current?.isPinching || gestureRef.current?.isPanning
              ? "none"
              : "transform 0.2s ease-out",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
        />
      )}
    </div>
  );
};

export default MediaViewer;
